import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useChangePassword } from "../../hooks/queries/useProfile";
import { setUser } from "../../store/slices/authSlice";
import Toast from "./Toast";

const PASSWORD_RULES = {
  min: 8,
  capital: /[A-Z]/,
  small: /[a-z]/,
  number: /[0-9]/,
  special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
};

const passwordSchema = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .required("New password is required")
    .min(PASSWORD_RULES.min, "Password must be at least 8 characters")
    .matches(PASSWORD_RULES.capital, "Must contain at least one capital letter")
    .matches(PASSWORD_RULES.small, "Must contain at least one small letter")
    .matches(PASSWORD_RULES.number, "Must contain at least one number")
    .matches(PASSWORD_RULES.special, "Must contain at least one special character")
    .test(
      "not-old",
      "New password must be different from current password",
      function (value) {
        return !value || value !== this.parent.currentPassword;
      }
    ),
  confirmNewPassword: Yup.string()
    .required("Confirm new password is required")
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .min(PASSWORD_RULES.min, "Password must be at least 8 characters")
    .matches(PASSWORD_RULES.capital, "Must contain at least one capital letter")
    .matches(PASSWORD_RULES.small, "Must contain at least one small letter")
    .matches(PASSWORD_RULES.number, "Must contain at least one number")
    .matches(PASSWORD_RULES.special, "Must contain at least one special character"),
});

const ChangePasswordModal = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const isForceChangeMode = user?.firstLogin === true && user?.role?.toLowerCase() !== "superadmin";

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState("success");
  const changePasswordMutation = useChangePassword();

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    validationSchema: passwordSchema,
    onSubmit: async (values) => {
      try {
        await changePasswordMutation.mutateAsync({
          oldPassword: values.currentPassword,
          newPassword: values.newPassword,
          confirmNewPassword: values.confirmNewPassword,
        });
        setToastMessage("Password changed successfully");
        setToastSeverity("success");
        formik.resetForm();
        dispatch(setUser({ firstLogin: false, isVerified: true }));
        try {
          const stored = localStorage.getItem("user");
          if (stored) {
            const parsed = JSON.parse(stored);
            localStorage.setItem("user", JSON.stringify({ ...parsed, firstLogin: false, isVerified: true }));
          }
        } catch (_) {}
        onSuccess?.();
        setTimeout(() => {
          onClose();
        }, 800);
      } catch (error) {
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to change password";
        setToastMessage(msg);
        setToastSeverity("error");
      }
    },
  });

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleClose = () => {
    if (isForceChangeMode) return;
    formik.resetForm();
    onClose();
  };

  return (
    <>
      <Dialog
        open={open || isForceChangeMode}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ensure your account is using a strong password for security.
          </Typography>
          <Box
            component="form"
            id="change-password-form"
            onSubmit={formik.handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
          >
            <TextField
              fullWidth
              label="Current Password"
              name="currentPassword"
              type={showPasswords.current ? "text" : "password"}
              value={formik.values.currentPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.currentPassword &&
                Boolean(formik.errors.currentPassword)
              }
              helperText={
                formik.touched.currentPassword && formik.errors.currentPassword
              }
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => togglePasswordVisibility("current")}
                    edge="end"
                  >
                    {showPasswords.current ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                ),
              }}
            />
            <TextField
              fullWidth
              label="New Password"
              name="newPassword"
              type={showPasswords.new ? "text" : "password"}
              value={formik.values.newPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.newPassword &&
                Boolean(formik.errors.newPassword)
              }
              helperText={
                formik.touched.newPassword && formik.errors.newPassword
              }
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => togglePasswordVisibility("new")}
                    edge="end"
                  >
                    {showPasswords.new ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              name="confirmNewPassword"
              type={showPasswords.confirm ? "text" : "password"}
              value={formik.values.confirmNewPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.confirmNewPassword &&
                Boolean(formik.errors.confirmNewPassword)
              }
              helperText={
                formik.touched.confirmNewPassword &&
                formik.errors.confirmNewPassword
              }
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => togglePasswordVisibility("confirm")}
                    edge="end"
                  >
                    {showPasswords.confirm ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {/* <Button onClick={handleClose}>Cancel</Button> */}
          <Button
            type="submit"
            form="change-password-form"
            variant="contained"
            disabled={changePasswordMutation.isPending}
          >
            {(changePasswordMutation.isPending ||
              changePasswordMutation.isLoading) && (
              <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
            )}
            Update Password
          </Button>
        </DialogActions>
      </Dialog>
      <Toast
        open={!!toastMessage}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </>
  );
};

export default ChangePasswordModal;
